import api from './api';
import { type PrestacaoComprovante } from './prestacaoContaService';

const base = '/prestacao-contas/relatorios';

export type PrestacaoItemResumo = {
  itemId: string;
  titulo?: string;
  tipo?: string;
  unidade?: string;
  quantidade?: number;
  valorUnitario?: number;
  valor?: number;
};

export type PrestacaoEtapaResumo = {
  cronogramaId: string;
  dataPrevista?: string;
  atividade?: string;
};

export type PrestacaoMetaResumo = {
  metaId: string;
  titulo?: string;
  descricao?: string;
  valor?: number;
  periodo?: string;
  itens: PrestacaoItemResumo[];
  etapas: PrestacaoEtapaResumo[];
};

export type PrestacaoRelatoriosCabecalho = {
  pcId: string;
  planoTrabalhoId: string;
  tipoPc: 'UNICA' | 'PARCIAL';
  numeroPc: number;
  mes?: string;
  pcUnica: boolean;
  ehPcFinal: boolean;
  protocoloPc?: string;
  planoTitulo?: string;
  numeroProtocolo?: string;
  modalidade?: string;
  objetoParceria?: string;
  valorPlano?: number;
  periodoInicio?: string;
  periodoFim?: string;
  contaBancaria?: string;
  banco?: string;
  agencia?: string;
  instituicaoNome?: string;
  instituicaoCnpj?: string;
  instituicaoTelefone?: string;
  instituicaoEmail?: string;
  dirigenteNome?: string;
  dirigenteCpf?: string;
  dirigenteCargo?: string;
  temItemInvestimento: boolean;
  metas: PrestacaoMetaResumo[];
};

export type PrestacaoExecucaoFisicaMeta = {
  id: string | null;
  metaId: string;
  metaTitulo?: string;
  valorMetaPlanejado?: number;
  quantidadeExecutada?: number | null;
  executadoAtePeriodo?: number;
  usuarioRegistro?: string;
};

export type PrestacaoPcFinanceiro = {
  pcId: string;
  numero: number;
  tipo: 'UNICA' | 'PARCIAL';
  mes?: string;
  saldoAnterior: number;
  valorRecebido: number;
  contrapartida: number;
  rendimentoAplicacao: number;
  valorUtilizado: number;
  saldo: number;
  /** Pagamentos das etapas declarados como recurso próprio — conferência, fora do saldo. */
  pagamentosRecursoProprio: number;
};

export type PrestacaoRelatoNarrativo = {
  id: string | null;
  acoesExecutadas?: string;
  alcanceObjetivos?: string;
  conclusao?: string;
  justificativaAtrasos?: string;
  medidasAdotadas?: string;
  finalizado: boolean;
  usuarioAtualizacao?: string;
  /** Relatório final (PC final/única): ações desenvolvidas no período. */
  acoesDesenvolvidasPeriodo?: string;
};

export type PrestacaoBem = {
  id: string;
  itemId?: string;
  documentoNumero?: string;
  documentoTipo?: string;
  dataDocumento?: string;
  especificacao?: string;
  /** Número de patrimônio do bem. */
  tombamento?: string;
  quantidade?: number;
  valorUnitario?: number;
  valorTotal?: number;
  situacao: 'ADQUIRIDO' | 'DEVOLVIDO';
  bemOriginalId?: string;
  dataDevolucao?: string;
  devolucaoSolicitadaEm?: string;
  devolucaoSolicitadaPor?: string;
  usuarioLancamento?: string;
};

export type PrestacaoBemSolicitacao = {
  bemId: string;
  planoTrabalhoId: string;
  pcId: string;
  planoTitulo?: string;
  instituicaoNome?: string;
  especificacao?: string;
  documentoNumero?: string;
  valorTotal?: number;
  devolucaoSolicitadaEm: string;
  devolucaoSolicitadaPor?: string;
};

export type PrestacaoAlcanceMeta = {
  id: string | null;
  metaId: string;
  metaTitulo?: string;
  valorMetaPlanejado?: number;
  quantidadeRealizada?: string | null;
  percentualAlcance?: number | null;
  meioAfericao?: string;
  justificativa?: string;
};

export type PrestacaoRemuneracaoEquipe = {
  id: string;
  funcaoCargo?: string;
  valorRemuneracao?: number;
  ordem: number;
};

export type PrestacaoItemExecucao = {
  id: string | null;
  itemId: string;
  itemTitulo?: string;
  itemValor?: number;
  valorExecutado?: number | null;
  usuarioRegistro?: string;
  comprovantes: PrestacaoComprovante[];
};

export type PrestacaoMetaSelecao = {
  metaId: string;
  metaTitulo?: string;
  metaValor?: number;
  selecionada: boolean;
  itens: PrestacaoItemExecucao[];
};

const getCabecalho = async (pcId: string): Promise<PrestacaoRelatoriosCabecalho> => {
  const res = await api.get(`${base}/pcs/${pcId}/cabecalho`);
  return res.data;
};

// ── Anexo 2 -- execucao fisica (por meta) ───────────────────────────────────

const getExecucaoFisica = async (pcId: string): Promise<PrestacaoExecucaoFisicaMeta[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/execucao-fisica`);
  return res.data;
};

const salvarExecucaoFisica = async (pcId: string, itens: { metaId: string; quantidadeExecutada: number | null }[]) => {
  const res = await api.put(`${base}/pcs/${pcId}/execucao-fisica`, itens);
  return res.data as PrestacaoExecucaoFisicaMeta[];
};

// ── Anexo 2 -- selecao de metas e execucao por item (etapa) ────────────────

const getMetasSelecionadas = async (pcId: string): Promise<PrestacaoMetaSelecao[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/metas-executadas`);
  return res.data;
};

const selecionarMeta = async (pcId: string, metaId: string): Promise<PrestacaoMetaSelecao[]> => {
  const res = await api.post(`${base}/pcs/${pcId}/metas-executadas`, { metaId });
  return res.data;
};

const removerMeta = async (pcId: string, metaId: string): Promise<PrestacaoMetaSelecao[]> => {
  const res = await api.delete(`${base}/pcs/${pcId}/metas-executadas/${metaId}`);
  return res.data;
};

const salvarItensExecucao = async (pcId: string, itens: { itemId: string; valorExecutado: number | null }[]): Promise<PrestacaoItemExecucao[]> => {
  const res = await api.put(`${base}/pcs/${pcId}/itens-execucao`, itens);
  return res.data;
};

// ── Anexo 2 -- financeiro ───────────────────────────────────────────────────

const getFinanceiro = async (pcId: string): Promise<PrestacaoPcFinanceiro> => {
  const res = await api.get(`${base}/pcs/${pcId}/financeiro`);
  return res.data;
};

/** Tabela encadeada por plano: uma linha por PC, ordenada por número. */
const getFinanceiroConsolidado = async (planoId: string): Promise<PrestacaoPcFinanceiro[]> => {
  const res = await api.get(`${base}/plano/${planoId}/financeiro`);
  return Array.isArray(res.data) ? res.data : [];
};

// ── Anexo 3 ──────────────────────────────────────────────────────────────

const getRelatoNarrativo = async (pcId: string): Promise<PrestacaoRelatoNarrativo> => {
  const res = await api.get(`${base}/pcs/${pcId}/relato-narrativo`);
  return res.data;
};

const salvarRelatoNarrativo = async (
  pcId: string,
  payload: { acoesExecutadas: string; alcanceObjetivos: string; conclusao: string; justificativaAtrasos: string; medidasAdotadas: string; acoesDesenvolvidasPeriodo?: string; finalizar: boolean },
) => {
  const res = await api.put(`${base}/pcs/${pcId}/relato-narrativo`, payload);
  return res.data as PrestacaoRelatoNarrativo;
};

// ── Anexo 4 / 7 -- bens ──────────────────────────────────────────────────

const getBens = async (pcId: string, situacao?: 'ADQUIRIDO' | 'DEVOLVIDO'): Promise<PrestacaoBem[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/bens`, { params: situacao ? { situacao } : undefined });
  return res.data;
};

export type PrestacaoBemPayload = {
  itemId?: string | null;
  documentoNumero: string;
  documentoTipo?: string;
  dataDocumento?: string | null;
  especificacao?: string;
  tombamento: string;
  quantidade: number | null;
  valorUnitario: number;
};

const criarBem = async (pcId: string, payload: PrestacaoBemPayload) => {
  const res = await api.post(`${base}/pcs/${pcId}/bens`, payload);
  return res.data as PrestacaoBem;
};

const atualizarBem = async (bemId: string, payload: PrestacaoBemPayload) => {
  const res = await api.put(`${base}/bens/${bemId}`, payload);
  return res.data as PrestacaoBem;
};

const excluirBem = async (bemId: string) => {
  await api.delete(`${base}/bens/${bemId}`);
};

const marcarDevolucao = async (bemId: string, dataDevolucao: string) => {
  const res = await api.post(`${base}/bens/${bemId}/devolucao`, { dataDevolucao });
  return res.data as PrestacaoBem;
};

/** Convenios/Admin solicitam a devolucao de um bem especifico a instituicao. */
const solicitarDevolucao = async (bemId: string) => {
  const res = await api.post(`${base}/bens/${bemId}/solicitar-devolucao`);
  return res.data as PrestacaoBem;
};

/** Bens com devolucao solicitada e ainda nao devolvidos, para a area de trabalho do usuario. */
const getSolicitacoesDevolucaoPendentes = async (): Promise<PrestacaoBemSolicitacao[]> => {
  const res = await api.get(`${base}/bens/solicitacoes-pendentes`);
  return Array.isArray(res.data) ? res.data : [];
};

// ── Anexo 6 ──────────────────────────────────────────────────────────────

const getAlcanceMetas = async (pcId: string): Promise<PrestacaoAlcanceMeta[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/alcance-metas`);
  return res.data;
};

const salvarAlcanceMetas = async (
  pcId: string,
  itens: { metaId: string; quantidadeRealizada: string | null; percentualAlcance: number | null; meioAfericao: string; justificativa: string }[],
) => {
  const res = await api.put(`${base}/pcs/${pcId}/alcance-metas`, itens);
  return res.data as PrestacaoAlcanceMeta[];
};

/** Declaração de despesa com RH e aluguel (1ª PC / PC única). */
const salvarDeclaracaoDespesas = async (pcId: string, rh: boolean, aluguel: boolean) => {
  await api.put(`${base}/pcs/${pcId}/despesas-declaradas`, { rh, aluguel });
};

// ── Anexo VIII ───────────────────────────────────────────────────────────

const getRemuneracoes = async (pcId: string): Promise<PrestacaoRemuneracaoEquipe[]> => {
  const res = await api.get(`${base}/pcs/${pcId}/remuneracoes`);
  return res.data;
};

const criarRemuneracao = async (pcId: string, payload: { funcaoCargo: string; valorRemuneracao: number | null }) => {
  const res = await api.post(`${base}/pcs/${pcId}/remuneracoes`, payload);
  return res.data as PrestacaoRemuneracaoEquipe;
};

const atualizarRemuneracao = async (id: string, payload: { funcaoCargo: string; valorRemuneracao: number | null }) => {
  const res = await api.put(`${base}/remuneracoes/${id}`, payload);
  return res.data as PrestacaoRemuneracaoEquipe;
};

const excluirRemuneracao = async (id: string) => {
  await api.delete(`${base}/remuneracoes/${id}`);
};

// ── Downloads ────────────────────────────────────────────────────────────
// URLs relativas consumidas por <AuthenticatedFileButton action="download">.

const downloadUrl = (pcId: string, anexo: '2' | '3' | '4' | '5' | '6' | '7' | '8', formato: 'pdf' | 'xlsx') =>
  `${base}/pcs/${pcId}/anexo${anexo}/${formato}`;

export default {
  getCabecalho,
  getExecucaoFisica,
  salvarExecucaoFisica,
  getMetasSelecionadas,
  selecionarMeta,
  removerMeta,
  salvarItensExecucao,
  getFinanceiro,
  getFinanceiroConsolidado,
  getRelatoNarrativo,
  salvarRelatoNarrativo,
  getBens,
  criarBem,
  atualizarBem,
  excluirBem,
  marcarDevolucao,
  solicitarDevolucao,
  getSolicitacoesDevolucaoPendentes,
  getAlcanceMetas,
  salvarAlcanceMetas,
  salvarDeclaracaoDespesas,
  getRemuneracoes,
  criarRemuneracao,
  atualizarRemuneracao,
  excluirRemuneracao,
  downloadUrl,
};
